// ProceduralCreature: one creature, drawn entirely in code from its genome and animated from a
// CreaturePose (see pose.ts). The container origin is the creature's feet centre, so callers
// just place it on the ground.
//
// Display tree (each part is its own display object so it can animate independently):
//   this
//   ├─ spotlight (lazy)          soft cone + pool of light, behind everything
//   ├─ shadow                    ground ellipse, shrinks while jumping
//   └─ rig                       feet-anchored: jump, squash/stretch, lean, flinch, dim tint
//      ├─ spinner                pivot at body centre: spin
//      │  ├─ glow (lazy)         additive halo (Glimmers always, others when pose.glow > 0)
//      │  ├─ back                tails, legs, antennae, tendrils, pods
//      │  ├─ body                body, horns/ears, belly, markings, blush
//      │  ├─ accessoryBack       scarf (behind the face)
//      │  ├─ face                eyes (white, pupil, lid), happy eyes, mouth
//      │  ├─ wings               between body and face (behind the eyes)
//      │  ├─ front               claws, flippers
//      │  ├─ accessory           hat/scarf/glasses/bow/crown
//      │  └─ shush (lazy)        finger-to-mouth + "shh" puffs
//      └─ zzz (lazy)             floating Z's while sleeping (not spun)
//
// Per-frame work is transforms only; Graphics are redrawn only when a quantized value changes
// (hue in 4° steps, mouth openness in 1/8 steps, Wobbler ripple in 1/10 steps × 16 phases).
// Form-specific moving parts (rotor hats, LEDs, lit keys, flames) animate through Anatomy.tick,
// which also only touches transforms / alpha / visibility.
//
// Traits: the body height is size × genome.sizeScale (outline width stays tied to `size`, so
// neighbouring creatures keep the same bold line); genome.aura adds a resting halo, genome.wobble
// a slow jelly squash that follows the breath, genome.lidRest sleepy lids, genome.sparkles eye
// highlights, genome.shimmer a halo that drifts through the palette.
import { Container, Graphics } from "pixi.js";
import { INK, hueShift, mix } from "../palette";
import { STEP_COLORS, drawKeys, stepKeyLayout } from "./parts";
import type { AccessoryKind, CreaturePose } from "./pose";
import type { CreatureGenome } from "./genome";
import { buildAnatomy, type Anatomy, type Colors, type Geo } from "./anatomy";
import { drawAccessory } from "./accessories";
import { clamp, disc, inkLine, lerp, noodle, smoothstep } from "./draw";

const WHITE = 0xffffff;
const MOUTH_DARK = 0x5a1f33;
const TONGUE = 0xff7f9f;
const BEAK = 0xe69f00;
const TAU = Math.PI * 2;

export interface CreatureOptions {
  /** Nominal body height in px (default 64); the drawn height is size × genome.sizeScale. */
  size?: number;
  accessory?: AccessoryKind;
}

export class ProceduralCreature extends Container {
  readonly genome: CreatureGenome;
  readonly size: number;

  private readonly H: number;
  private readonly ow: number;
  private readonly anat: Anatomy;
  private readonly geo: Geo;
  private readonly idlePhase: number;

  private readonly shadow = new Graphics();
  private spotlightG: Graphics | null = null;
  private readonly rig = new Container();
  private readonly spinner = new Container();
  private glowG: Graphics | null = null;
  private readonly face = new Container();
  private readonly eyes: Container[] = [];
  private readonly pupils: Graphics[] = [];
  private readonly lids: Graphics[] = [];
  private readonly eyeRadius: number;
  private readonly happyEyes = new Graphics();
  private readonly mouth = new Graphics();
  private readonly accessoryG = new Graphics();
  /** Scarves sit behind the face so an open mouth / eyes stay visible. */
  private readonly accessoryBackG = new Graphics();
  private shushG: Graphics | null = null;
  private zzzLayer: Container | null = null;
  private zzz: Graphics[] = [];

  private colors: Colors;
  private accessory: AccessoryKind = "none";
  private accessoryDrawn = false;
  private time = 0;
  private hueQ = 0;
  private dimQ = 0;
  private mouthKey = -1;
  private rippleKey = 0;
  private rippleAmt = 0;
  private ripplePhase = 0;
  private readonly restGlow: number;
  private knobAngle = 0;

  constructor(genome: CreatureGenome, opts: CreatureOptions = {}) {
    super();
    this.genome = genome;
    this.size = opts.size ?? 64;
    this.H = this.size * (genome.sizeScale || 1);
    this.ow = Math.max(1.25, (this.size * 3) / 64);
    this.idlePhase = ((genome.seed % 997) / 997) * TAU;
    this.anat = buildAnatomy(genome, this.H, this.ow);
    this.geo = this.anat.geo;
    const { geo, H } = this;

    // Ground shadow (drawn once).
    this.shadow.ellipse(0, 0, geo.halfW * 0.85, H * 0.07).fill({ color: INK, alpha: 0.18 });
    this.addChild(this.shadow, this.rig);

    this.spinner.pivot.set(0, geo.centerY);
    this.spinner.position.set(0, geo.centerY);
    this.rig.addChild(this.spinner);
    // Wings sit between the body and the face: in front of the body, behind the eyes.
    const wings = new Container();
    for (const l of this.anat.limbs) {
      if (l.role === "wing" && l.node.parent === this.anat.front) wings.addChild(l.node);
    }
    this.spinner.addChild(
      this.anat.back,
      this.anat.body,
      wings,
      this.accessoryBackG,
      this.face,
      this.anat.front,
      this.accessoryG,
    );

    // Eyes: white + pupil (static), lid (scaled from the top of the eye by blink).
    const style = geo.eyeStyle ?? "normal";
    const one = genome.eyeCount === 1 && style === "normal";
    const r = one ? geo.eyeR * 1.25 : geo.eyeR;
    this.eyeRadius = r;
    const xs = one ? [0] : [-geo.eyeDx, geo.eyeDx];
    for (const x of xs) {
      const eye = new Container();
      eye.position.set(x, geo.eyeY);
      const white = new Graphics();
      const pupil = new Graphics();
      if (style === "knob") {
        // Knob eye: cream dial with tick marks; the pupil is the knob cap whose pointer turns.
        disc(white, 0, 0, r, 0xf4efe1, this.ow * 0.85);
        for (let k = 0; k < 7; k++) {
          const a = -2.4 + (k / 6) * 4.8;
          white
            .moveTo(Math.sin(a) * r * 0.78, -Math.cos(a) * r * 0.78)
            .lineTo(Math.sin(a) * r * 0.95, -Math.cos(a) * r * 0.95)
            .stroke({ width: Math.max(0.8, this.ow * 0.4), color: INK });
        }
        const pr = r * 0.62;
        pupil
          .circle(0, 0, pr)
          .fill({ color: 0x2b2540 })
          .stroke({ width: this.ow * 0.6, color: INK });
        pupil
          .moveTo(0, 0)
          .lineTo(0, -pr * 0.85)
          .stroke({ width: Math.max(1.2, pr * 0.22), color: WHITE, cap: "round" });
        pupil.circle(-pr * 0.4, -pr * 0.35, pr * 0.16).fill({ color: WHITE, alpha: 0.6 });
      } else if (style === "screen") {
        // Pixel eye on an LCD screen (the screen itself is part of the body).
        const w = r * 0.9,
          h = r * 1.3;
        pupil.roundRect(-w / 2, -h / 2, w, h, w * 0.18).fill({ color: 0x2a3a18 });
        pupil.rect(-w * 0.3, -h * 0.38, w * 0.28, w * 0.28).fill({ color: geo.screenColor ?? 0xb9d36c });
      } else {
        disc(white, 0, 0, r, WHITE, this.ow * 0.85);
        const pr = r * genome.pupilSize;
        pupil.circle(0, r * 0.12, pr).fill({ color: INK });
        pupil.circle(-pr * 0.35, r * 0.12 - pr * 0.4, pr * 0.38).fill({ color: WHITE });
        // Bright sounds: extra sparkles.
        if (genome.sparkles >= 1) pupil.circle(pr * 0.4, r * 0.12 + pr * 0.4, pr * 0.16).fill({ color: WHITE });
        if (genome.sparkles >= 2) {
          const sr = r * (genome.sparkles >= 3 ? 0.42 : 0.3);
          pupil
            .star(r * 0.45, -r * 0.45, 4, sr, sr * 0.3)
            .fill({ color: WHITE })
            .stroke({ width: Math.max(0.6, this.ow * 0.3), color: INK, alpha: 0.5 });
        }
      }
      const lid = new Graphics();
      lid.position.set(0, -r - this.ow * 0.5);
      lid.visible = false;
      eye.addChild(white, pupil, lid);
      this.face.addChild(eye);
      this.eyes.push(eye);
      this.pupils.push(pupil);
      this.lids.push(lid);
      // Happy "^" eyes for cheering.
      const happy = [x - r * 0.8, geo.eyeY + r * 0.25, x, geo.eyeY - r * 0.55, x + r * 0.8, geo.eyeY + r * 0.25];
      if (geo.faceInk !== undefined) noodle(this.happyEyes, happy, this.ow * 1.1, geo.faceInk, this.ow * 0.6);
      else inkLine(this.happyEyes, happy, this.ow * 1.2, false);
    }
    this.happyEyes.visible = false;
    this.mouth.position.set(0, geo.mouthY);
    this.face.addChild(this.happyEyes, this.mouth);

    this.colors = this.computeColors(0);
    this.recolor();
    this.drawMouth(0, 0);
    this.mouthKey = 0;
    this.restGlow =
      (genome.species === "glimmer" ? 0.3 : 0) + (geo.selfGlow ?? 0) + Math.max(0, (genome.aura ?? 0) - 0.3) * 0.65;
    if (this.restGlow > 0.15) this.ensureGlow();
    this.setAccessory(opts.accessory ?? "none");
  }

  /** Change the cosmetic accessory (redraws only the accessory layer). */
  setAccessory(kind: AccessoryKind): void {
    if (kind === this.accessory && this.accessoryDrawn) return;
    this.accessory = kind;
    this.accessoryDrawn = true;
    const back = kind === "scarf";
    (back ? this.accessoryG : this.accessoryBackG).clear();
    drawAccessory(
      back ? this.accessoryBackG : this.accessoryG,
      kind,
      this.geo,
      this.H,
      this.ow,
      this.genome.species,
      this.genome.eyeCount,
    );
  }

  /** Apply a pose. Call once per frame. */
  update(p: CreaturePose, dtSeconds: number): void {
    const t = (this.time += dtSeconds);
    const { H, geo, genome } = this;
    const species = genome.species;
    const state = p.state;
    const hitting = state === "hit" || state === "accent";
    const hitAmt = hitting ? 1 - clamp(p.phase, 0, 1) : 0;
    const flinch = clamp(p.flinch, 0, 1);
    const cheer = state === "cheer";
    const sleeping = state === "sleep";

    // Color: hue rotation from pitch, quantized to keep redraws rare.
    const hq = Math.round(p.hueShift / 4) * 4;
    if (hq !== this.hueQ) {
      this.hueQ = hq;
      this.colors = this.computeColors(hq);
      this.recolor();
    }

    // Squash & stretch around the feet; volume-preserving (sx * sy ≈ 1).
    const bob = clamp(p.bob, -1, 1);
    const sq =
      clamp(clamp(p.squash, -1, 1) * (geo.squashScale ?? 1), -1.2, 1.2) + flinch * 0.25 + (bob < 0 ? -bob * 0.06 : 0);
    const sx = 1 + sq * 0.25;
    const sy = 1 / sx;
    const br = clamp(p.breath, -1, 1) * 0.025;
    const jig = species !== "wobbler" && p.ripple > 0 ? Math.sin(t * 24) * clamp(p.ripple, 0, 1) * 0.04 : 0;
    const s = p.scale;
    // Jelly wobble (trait): slow squash that follows the breath plus a gentle ~0.7 Hz sway.
    const wob = sleeping
      ? 0
      : (genome.wobble ?? 0) * (0.05 * clamp(p.breath, -1, 1) + 0.015 * Math.sin(t * 4.4 + this.idlePhase));
    this.rig.scale.set(s * sx * (1 - br * 0.5 + jig + wob), s * sy * (1 + br - jig - wob));

    let y = -p.jump * H * s - (bob > 0 ? bob * 0.04 * H * s : 0);
    if (geo.floats) y -= (0.03 + 0.03 * Math.sin(t * 0.9 + this.idlePhase)) * H * s;
    this.rig.position.set(flinch > 0 ? Math.sin(t * 75) * flinch * 0.05 * H : 0, y);
    this.rig.rotation = clamp(p.lean, -1, 1) * 0.16 - flinch * 0.1;
    this.spinner.rotation = p.spin + (sleeping ? 0.07 : 0);

    // Dimming when another creature is soloed: darken via tint + fade a little.
    const dq = Math.round(clamp(p.dim, 0, 1) * 20);
    if (dq !== this.dimQ) {
      this.dimQ = dq;
      this.rig.tint = mix(WHITE, 0x707088, dq / 20);
      this.alpha = 1 - (dq / 20) * 0.35;
    }

    // Shadow shrinks/fades while airborne.
    const lift = p.jump + (geo.floats ? 0.35 : 0);
    const shs = (s * sx) / (1 + lift * 0.9);
    this.shadow.scale.set(shs, shs);
    this.shadow.alpha = 1 / (1 + lift * 1.5);

    // Spotlight (solo).
    if (p.spotlight) {
      const sp = this.ensureSpotlight();
      sp.visible = true;
      sp.alpha = 0.85 + 0.15 * Math.sin(t * 1.2);
    } else if (this.spotlightG) this.spotlightG.visible = false;

    // Glow: Glimmers always glow faintly; any creature can glow when pose.glow > 0.
    const glow = clamp(p.glow, 0, 1);
    const glowA = this.restGlow * (sleeping ? 0.5 : 1) + glow * 0.9;
    if (glowA > 0.01) {
      const gg = this.ensureGlow();
      gg.visible = true;
      gg.alpha = Math.min(1, glowA);
      const gs = 1 + glow * 0.15 + Math.sin(t * 1.3 + this.idlePhase) * 0.02;
      gg.scale.set(gs, gs);
      if (genome.shimmer && this.colors.palette.length) {
        // Rainbow shimmer: drift through the palette, one colour every ~4 s.
        const pl = this.colors.palette,
          u = (t * 0.25 + this.idlePhase) % pl.length;
        const i0 = Math.floor(u);
        gg.tint = mix(pl[i0], pl[(i0 + 1) % pl.length], u - i0);
      }
    } else if (this.glowG) this.glowG.visible = false;

    // Wobbler body ripple (redraw only on quantized change).
    if (species === "wobbler") {
      const rq = Math.round(clamp(p.ripple, 0, 1) * 10);
      const ph = rq > 0 ? Math.floor(t * 20) % 16 : 0;
      const key = rq * 100 + ph;
      if (key !== this.rippleKey) {
        this.rippleKey = key;
        this.rippleAmt = rq / 10;
        this.ripplePhase = (ph / 16) * TAU;
        this.anat.drawBody(this.colors, this.rippleAmt, this.ripplePhase);
      }
    }

    // Eyes: lids from blink (sleep closes them), happy arcs while cheering, pupils follow lean.
    const calm = state === "idle" || state === "anticipate" || state === "hold";
    const blink = sleeping
      ? 1
      : Math.max(clamp(p.blink, 0, 1), flinch * 0.8, state === "shush" ? 0.35 : 0, calm ? (genome.lidRest ?? 0) : 0);
    const showHappy = cheer;
    this.happyEyes.visible = showHappy;
    const look = clamp(p.lean, -1, 1) * this.eyeRadius * 0.22;
    for (let i = 0; i < this.eyes.length; i++) {
      this.eyes[i].visible = !showHappy;
      const lid = this.lids[i];
      lid.visible = blink > 0.03;
      lid.scale.y = blink;
      this.pupils[i].x = look;
      this.pupils[i].y = state === "anticipate" ? -this.eyeRadius * 0.1 : 0;
    }
    if (geo.eyeStyle === "knob") {
      // Knob eyes turn: slow drift at idle, cranked up on hits, held high while a note sustains.
      const target = sleeping
        ? -2.3
        : state === "hold"
          ? 2.1
          : hitting
            ? -0.4 + hitAmt * 2.4
            : cheer
              ? Math.sin(t * 6) * 1.5
              : Math.sin(t * 0.6 + this.idlePhase) * 0.5;
      this.knobAngle += (target - this.knobAngle) * Math.min(1, dtSeconds * (hitting ? 30 : 6));
      for (let i = 0; i < this.pupils.length; i++) {
        this.pupils[i].x = 0;
        this.pupils[i].rotation = this.knobAngle * (i ? 1 : -1);
      }
    }

    // Mouth: 0 normal, 1 shush "o", 2 cheer grin, 3 sleep.
    const mode = state === "shush" ? 1 : cheer ? 2 : sleeping ? 3 : 0;
    const open = sleeping ? 0 : clamp(p.mouthOpen, 0, 1);
    const mk = Math.round(open * 8) + mode * 10;
    if (mk !== this.mouthKey) {
      this.mouthKey = mk;
      this.drawMouth(open, mode);
    }

    this.animateLimbs(p, t, hitAmt, glow);
    this.anat.tick?.(p, t, hitAmt);

    // Shush gesture.
    if (state === "shush") {
      const sh = this.ensureShush();
      sh.visible = true;
      sh.y = Math.sin(t * 1.6) * H * 0.006;
    } else if (this.shushG) this.shushG.visible = false;

    // Floating Zzz while sleeping.
    if (sleeping) {
      const layer = this.ensureZzz();
      layer.visible = true;
      for (let i = 0; i < this.zzz.length; i++) {
        const z = this.zzz[i];
        const ph = (p.t * 0.45 + i / this.zzz.length) % 1;
        z.position.set(
          geo.halfW * 0.35 + ph * H * 0.3 + Math.sin(ph * TAU) * H * 0.04,
          geo.topY + H * 0.1 - ph * H * 0.5,
        );
        z.alpha = Math.sin(Math.PI * ph);
        const zs = 0.5 + 0.6 * ph;
        z.scale.set(zs, zs);
      }
    } else if (this.zzzLayer) this.zzzLayer.visible = false;
  }

  override destroy(options?: Parameters<Container["destroy"]>[0]): void {
    super.destroy(options ?? { children: true });
  }

  // -------------------------------------------------------------------------------------------

  private animateLimbs(p: CreaturePose, t: number, hitAmt: number, glow: number): void {
    const state = p.state;
    const hitting = hitAmt > 0;
    const cheer = state === "cheer";
    const phase = clamp(p.phase, 0, 1);
    const limbs = this.anat.limbs;
    for (let i = 0; i < limbs.length; i++) {
      const L = limbs[i];
      const s = L.side;
      const k = L.index + s + this.idlePhase;
      let a = L.rest;
      switch (L.role) {
        case "arm":
        case "pod":
          if (cheer) a = 2.5 + Math.sin(t * 14 + s) * 0.3;
          else if (hitting) a = L.rest + hitAmt * 1.0;
          else if (state === "anticipate") a = L.rest - 0.35 * phase;
          else a = L.rest + Math.sin(t * 0.9 + k) * (L.role === "pod" ? 0.08 : 0.03);
          break;
        case "wing":
          if (cheer) a = 1.6 + Math.sin(t * 22) * 0.5;
          else if (hitting) a = L.rest + hitAmt * (0.9 + 0.5 * Math.sin(t * 40));
          else if (state === "anticipate") a = L.rest - 0.2 * phase;
          else a = L.rest + Math.sin(t * 0.9 + k) * 0.03;
          break;
        case "flipper": {
          const clap = -1.2;
          if (hitting)
            a = lerp(clap, L.rest, smoothstep(0.15, 0.75, phase)); // clap at the onset
          else if (state === "anticipate")
            a = L.rest + 0.6 * smoothstep(0, 1, phase); // wind up wide
          else if (cheer) a = lerp(clap, 1.4, 0.5 + 0.5 * Math.sin(t * 14));
          else if (state === "hold") a = clap * 0.7;
          else a = L.rest + Math.sin(t * 0.9 + k) * 0.035;
          break;
        }
        case "claw": {
          // Tilt > 0 = outward/up. Jaw open amount 0 = shut.
          let tilt: number, jaw: number;
          if (cheer) {
            tilt = 0.35 + Math.sin(t * 10 + s) * 0.25;
            jaw = 0.3 + 0.3 * Math.abs(Math.sin(t * 12));
          } else if (hitting) {
            tilt = 0.1 + hitAmt * 0.25;
            jaw = 0.6 * smoothstep(0.05, 0.6, phase);
          } // SNAP shut at onset
          else if (state === "anticipate") {
            tilt = -0.15 * phase;
            jaw = 0.35 + 0.4 * phase;
          } else if (state === "hold") {
            tilt = 0.1;
            jaw = 0;
          } else if (state === "sleep") {
            tilt = -0.1;
            jaw = 0.05;
          } else {
            tilt = Math.sin(t * 0.8 + k) * 0.03;
            jaw = 0.35 + Math.sin(t * 1.1 + k) * 0.03;
          }
          L.node.rotation = s * tilt;
          if (L.jaw) L.jaw.rotation = -s * jaw;
          continue;
        }
        case "leg":
          if (s === 0) continue; // Chirper legs are static
          a = p.jump * 0.5 + (hitting ? Math.sin(t * 30 + s) * 0.08 * hitAmt : 0);
          break;
        case "tail":
          a = Math.sin(t * 1.0 + k) * 0.035;
          if (hitting)
            a += Math.sin(p.t * 30) * 0.45 * hitAmt; // flick
          else if (cheer) a += Math.sin(t * 16) * 0.3;
          else if (state === "anticipate") a -= 0.25 * phase;
          break;
        case "antenna": {
          let r = L.rest * 0.4 + Math.sin(t * 1.6 + L.index * 1.3 + this.idlePhase) * 0.06;
          if (hitting) r += Math.sin(t * 35 + L.index) * 0.3 * hitAmt;
          else if (cheer) r += Math.sin(t * 12 + L.index) * 0.25;
          else if (state === "sleep") r = L.rest * 1.6 + (L.rest >= 0 ? 0.3 : -0.3);
          L.node.rotation = r;
          continue;
        }
        case "tendril": {
          let r =
            Math.sin(t * 1.0 + L.index * 0.9 + this.idlePhase) * (0.05 + 0.2 * hitAmt + 0.06 * glow) - p.lean * 0.1;
          if (cheer) r += Math.sin(t * 8 + L.index) * 0.3;
          L.node.rotation = r;
          continue;
        }
      }
      L.node.rotation = -s * a;
    }
  }

  private computeColors(hue: number): Colors {
    const g = this.genome;
    const base = hueShift(g.baseColor, hue);
    return {
      base,
      accent: hueShift(g.accentColor, hue),
      belly: hueShift(g.bellyColor, hue),
      light: mix(base, WHITE, 0.6),
      palette: (g.palette ?? []).map((c) => hueShift(c, hue)),
    };
  }

  /** Redraw every hue-dependent part. */
  private recolor(): void {
    const c = this.colors;
    for (const d of this.anat.drawers) d(c);
    this.anat.drawBody(c, this.rippleAmt, this.ripplePhase);
    const r = this.eyeRadius,
      R = r + this.ow * 0.5;
    const style = this.geo.eyeStyle ?? "normal";
    for (const lid of this.lids) {
      lid.clear();
      if (style === "screen") {
        // LCD "lid": screen colour wiping down, with a pixel line for closed eyes.
        lid.rect(-r * 0.6, R * 0.25, r * 1.2, r * 1.5).fill({ color: this.geo.screenColor ?? 0xb9d36c });
        lid.rect(-r * 0.5, R * 1.55, r, Math.max(1, r * 0.2)).fill({ color: 0x2a3a18 });
        continue;
      }
      lid
        .circle(0, R, R)
        .fill({ color: style === "knob" ? 0x2b2540 : c.base })
        .stroke({ width: this.ow * 0.85, color: INK });
      inkLine(
        lid,
        [-R * 0.7, R * 1.5, 0, R * 1.95, R * 0.7, R * 1.5],
        this.ow * 0.9,
        true,
        style === "knob" ? WHITE : INK,
      );
    }
    if (this.glowG) this.drawGlow(this.glowG);
    if (this.shushG) this.drawShush(this.shushG);
  }

  private drawMouth(open: number, mode: number): void {
    const g = this.mouth;
    const H = this.H,
      ow = this.ow;
    g.clear();
    const mstyle = this.geo.mouthStyle ?? "normal";
    if (mstyle === "keys") {
      this.drawKeysMouth(open, mode);
      return;
    }
    if (mstyle === "steps") {
      this.drawStepsMouth(open, mode);
      return;
    }
    if (mstyle === "beak") {
      // Beak: upper half fixed, lower half drops as it opens.
      const bs = this.geo.beakScale ?? this.genome.beakLength;
      const bw = H * 0.12 * Math.min(1, 0.6 + bs * 0.4),
        Lb = H * 0.1 * bs;
      const o = mode === 1 ? 0.25 : mode === 2 ? Math.max(open, 0.6) : open;
      const gap = o * Lb * 0.55;
      if (o > 0.05) {
        g.ellipse(0, gap * 0.6, bw * 0.3, gap * 0.7 + 1).fill({ color: MOUTH_DARK });
        g.poly([-bw * 0.4, gap * 0.4, bw * 0.4, gap * 0.4, 0, gap * 0.4 + Lb * 0.6])
          .fill({ color: BEAK })
          .stroke({ width: ow * 0.8, color: INK, join: "round" });
      }
      g.poly([-bw / 2, -Lb * 0.1, bw / 2, -Lb * 0.1, 0, Lb * (o > 0.05 ? 0.55 : 0.9)])
        .fill({ color: BEAK })
        .stroke({ width: ow * 0.8, color: INK, join: "round" });
      return;
    }
    const w = this.geo.mouthW;
    if (mode === 1) {
      // "o" for shush.
      g.ellipse(0, H * 0.01, Math.max(w * 0.12, H * 0.03), Math.max(w * 0.1, H * 0.025))
        .fill({ color: MOUTH_DARK })
        .stroke({ width: ow * 0.8, color: INK });
      return;
    }
    if (mode === 3) {
      // Sleepy small mouth.
      g.moveTo(-w * 0.2, 0)
        .quadraticCurveTo(0, w * 0.08, w * 0.2, 0)
        .stroke({ width: ow * 0.9, color: INK, cap: "round" });
      return;
    }
    const o = mode === 2 ? Math.max(open, 0.55) : open;
    const fangs = this.genome.teeth || this.geo.fangs;
    if (o < 0.08) {
      if (fangs)
        for (const sx of [-1, 1]) {
          const fx = sx * w * 0.22,
            fy = w * 0.075;
          g.poly([fx - w * 0.06, fy, fx + w * 0.06, fy, fx, fy + Math.max(H * 0.05, w * 0.14)])
            .fill({ color: WHITE })
            .stroke({ width: ow * 0.6, color: INK, join: "round" });
        }
      g.moveTo(-w / 2, 0)
        .quadraticCurveTo(0, w * 0.35, w / 2, 0)
        .stroke({ width: ow, color: INK, cap: "round" });
      return;
    }
    const d = o * w * 0.5 + w * 0.06; // depth of the open mouth
    const path = () =>
      g
        .moveTo(-w / 2, 0)
        .quadraticCurveTo(0, w * 0.12, w / 2, 0)
        .quadraticCurveTo(0, d * 2, -w / 2, 0)
        .closePath();
    path();
    g.fill({ color: MOUTH_DARK });
    if (d > H * 0.05) g.ellipse(0, d * 0.72, w * 0.2, d * 0.24).fill({ color: TONGUE });
    if (this.genome.species === "thumper") {
      // Two little teeth.
      for (const sx of [-1, 1])
        g.roundRect(sx * w * 0.12 - w * 0.05, w * 0.03, w * 0.1, Math.min(d * 0.35, H * 0.06), w * 0.02).fill({
          color: WHITE,
        });
    }
    if (fangs)
      for (const sx of [-1, 1]) {
        const fx = sx * w * 0.3,
          fy = w * 0.06;
        g.poly([fx - w * 0.06, fy - w * 0.02, fx + w * 0.06, fy - w * 0.02, fx, fy + Math.min(d * 0.5, H * 0.07)]).fill(
          { color: WHITE },
        );
      }
    path();
    g.stroke({ width: ow, color: INK, join: "round" });
  }

  /** Keyboard mouth (synth characters): a key strip smile; opening shows a dark mouth under the keys. */
  private drawKeysMouth(open: number, mode: number): void {
    const g = this.mouth,
      H = this.H,
      ow = this.ow;
    const w = this.geo.mouthW,
      n = this.geo.mouthKeys ?? 8,
      kh = Math.max(H * 0.065, w * 0.13);
    if (mode === 1) {
      g.ellipse(0, H * 0.01, H * 0.035, H * 0.03)
        .fill({ color: MOUTH_DARK })
        .stroke({ width: ow * 0.8, color: INK });
      return;
    }
    const o = mode === 3 ? 0 : mode === 2 ? Math.max(open, 0.55) : open;
    if (o >= 0.08) {
      const d = kh * 0.4 + o * H * 0.14;
      g.roundRect(-w / 2, -kh * 0.5, w, kh * 0.5 + d, Math.min(w * 0.2, H * 0.06))
        .fill({ color: MOUTH_DARK })
        .stroke({ width: ow, color: INK });
      if (d > H * 0.07) g.ellipse(0, d * 0.7, w * 0.22, d * 0.22).fill({ color: TONGUE });
    }
    // Lit key while singing.
    const lit = o >= 0.08 && mode !== 3 ? Math.round(o * 8) % n : -1;
    drawKeys(g, -w / 2, -kh * 0.5, w, kh, n, ow * 0.7, lit);
  }

  /**
   * Drum-machine grin (Boomer): the row of coloured step keys are the upper teeth, set in a dark
   * gum line with upturned corners; opening drops a dark mouth below the (fixed) key row.
   */
  private drawStepsMouth(open: number, mode: number): void {
    const g = this.mouth,
      H = this.H,
      ow = this.ow;
    const w = this.geo.mouthW,
      n = this.geo.mouthKeys ?? 8;
    const L = stepKeyLayout(w, n, H);
    const pad = Math.max(ow * 0.5, L.gap);
    const o = mode === 3 || mode === 1 ? 0 : mode === 2 ? Math.max(open, 0.55) : open;
    const d = o >= 0.08 ? o * H * (this.geo.mouthDepth ?? 0.1) : 0;
    const top = -L.kh / 2 - pad,
      bot = L.kh / 2 + pad * 1.2 + d;
    const r = Math.min(L.kh * 0.45, H * 0.04);
    g.roundRect(-w / 2 - pad, top, w + pad * 2, bot - top, r)
      .fill({ color: MOUTH_DARK })
      .stroke({ width: ow, color: INK });
    if (d > H * 0.045) g.ellipse(0, L.kh / 2 + d * 0.7, w * 0.18, d * 0.3).fill({ color: TONGUE });
    // Smile corners.
    for (const sx of [-1, 1])
      inkLine(
        g,
        [sx * (w / 2 + pad * 0.6), top + L.kh * 0.25, sx * (w / 2 + pad + H * 0.035), top - H * 0.02],
        ow * 0.9,
        false,
      );
    for (let i = 0; i < n; i++) {
      g.roundRect(L.keyX(i), -L.kh / 2, L.kw, L.kh, Math.min(L.kw, L.kh) * 0.22)
        .fill({ color: STEP_COLORS[L.group(i)] })
        .stroke({ width: Math.max(0.8, ow * 0.3), color: INK });
    }
    if (this.genome.teeth || this.geo.fangs) {
      for (const sx of [-1, 1]) {
        const fx = sx * (w / 2 - L.kw * 0.4),
          fy = L.kh / 2;
        g.poly([fx - L.kw * 0.35, fy, fx + L.kw * 0.35, fy, fx, fy + Math.max(H * 0.045, L.kh * 0.6)])
          .fill({ color: WHITE })
          .stroke({ width: ow * 0.6, color: INK, join: "round" });
      }
    }
  }

  private ensureGlow(): Graphics {
    if (!this.glowG) {
      const g = new Graphics();
      g.blendMode = "add";
      g.position.set(0, this.geo.centerY);
      this.drawGlow(g);
      this.spinner.addChildAt(g, 0);
      this.glowG = g;
    }
    return this.glowG;
  }

  private drawGlow(g: Graphics): void {
    g.clear();
    const R = Math.max(Math.min(this.geo.halfW, this.H * 0.6), this.H * 0.4) * 1.5;
    const col = mix(this.colors.light, 0xfff3a8, 0.4);
    // Drawn white and tinted, so shimmer can recolour it per frame without a redraw.
    for (let i = 0; i < 6; i++) g.circle(0, 0, R * (1 - i * 0.14)).fill({ color: WHITE, alpha: 0.09 });
    g.tint = col;
  }

  private ensureSpotlight(): Graphics {
    if (!this.spotlightG) {
      const g = new Graphics();
      const H = this.H,
        w = Math.max(this.geo.halfW, H * 0.4) * 1.5;
      g.poly([-H * 0.12, -H * 3, H * 0.12, -H * 3, w, 0, -w, 0]).fill({ color: 0xfff3a8, alpha: 0.13 });
      g.poly([-H * 0.06, -H * 3, H * 0.06, -H * 3, w * 0.6, 0, -w * 0.6, 0]).fill({ color: 0xfff3a8, alpha: 0.1 });
      g.ellipse(0, 0, w, H * 0.16).fill({ color: 0xfff3a8, alpha: 0.35 });
      this.addChildAt(g, 0);
      this.spotlightG = g;
    }
    return this.spotlightG;
  }

  private ensureShush(): Graphics {
    if (!this.shushG) {
      this.shushG = new Graphics();
      this.drawShush(this.shushG);
      this.spinner.addChild(this.shushG);
    }
    return this.shushG;
  }

  private drawShush(g: Graphics): void {
    g.clear();
    const H = this.H,
      ow = this.ow,
      my = this.geo.mouthY;
    // Finger raised over the mouth.
    const fw = H * 0.12,
      fh = H * 0.28;
    g.roundRect(-fw / 2, my - fh * 0.6, fw, fh, fw / 2)
      .fill({ color: this.colors.light })
      .stroke({ width: ow, color: INK });
    g.roundRect(-fw * 0.28, my - fh * 0.55, fw * 0.56, fw * 0.5, fw * 0.25).fill({ color: WHITE, alpha: 0.8 });
    // "shh" puffs.
    const x0 = Math.max(this.geo.mouthW * 0.5, H * 0.1) + H * 0.04;
    for (let i = 0; i < 3; i++) {
      const x = x0 + i * H * 0.06,
        r = H * (0.03 + i * 0.012);
      const cy = my - H * 0.02;
      // moveTo the arc start first, otherwise the path connects from the previous shape.
      g.moveTo(x + r * Math.cos(-0.9), cy + r * Math.sin(-0.9))
        .arc(x, cy, r, -0.9, 0.9)
        .stroke({ width: ow * 0.7, color: INK, cap: "round" });
    }
  }

  private ensureZzz(): Container {
    if (!this.zzzLayer) {
      const layer = new Container();
      const z = this.H * 0.16;
      for (let i = 0; i < 3; i++) {
        const g = new Graphics();
        noodle(g, [-z / 2, -z / 2, z / 2, -z / 2, -z / 2, z / 2, z / 2, z / 2], this.ow * 0.9, WHITE, this.ow * 0.7);
        layer.addChild(g);
        this.zzz.push(g);
      }
      this.rig.addChild(layer);
      this.zzzLayer = layer;
    }
    return this.zzzLayer;
  }
}
